import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, type MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import type { CustomerListItem } from '@models/customers';
import type { WaterOwner } from '@models/waters';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@services/confirmation.service';
import { CrmWaterOwnersService } from '@services/crm-water-owners.service';
import { CustomersService } from '@services/customers.service';
import { ToastService } from '@services/toast.service';
import { catchError, debounceTime, distinctUntilChanged, firstValueFrom, map, of, switchMap } from 'rxjs';

@Component({
  selector: 'app-water-owner-list',
  templateUrl: './water-owner-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslatePipe,
  ],
})
export class WaterOwnerListComponent {
  readonly waterId = input.required<number>();

  // Emits the current owner list (once loaded) so the parent can warn about missing service-order emails.
  readonly ownersChanged = output<WaterOwner[]>();

  private readonly ownersService = inject(CrmWaterOwnersService);
  private readonly customersService = inject(CustomersService);
  private readonly router = inject(Router);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);

  readonly busyId = signal<number | null>(null);
  readonly linking = signal(false);
  readonly displayedColumns = ['name', 'email', 'actions'];

  // Holds a raw string while typing; never keeps a picked object (reset right after linking).
  readonly searchControl = new FormControl<string | CustomerListItem | null>('');
  readonly ownerOptions = signal<CustomerListItem[]>([]);

  readonly ownersResource = rxResource({
    params: () => ({ waterId: this.waterId() }),
    stream: ({ params }) =>
      this.ownersService.getOwners(params.waterId).pipe(
        catchError(() => {
          this.toastService.showError(this.translate.instant('waters.owners.load-failed'));
          return of([] as WaterOwner[]);
        }),
      ),
  });

  readonly owners = computed(() => this.ownersResource.value() ?? []);
  readonly isLoading = this.ownersResource.isLoading;

  constructor() {
    // Surface the loaded owner list to the parent (skip the loading phase to avoid a false "missing email" flash).
    effect(() => {
      if (this.ownersResource.isLoading()) return;
      this.ownersChanged.emit(this.owners());
    });

    // Owner search → query the customers endpoint (any customer can be a water owner); exclude already-linked owners.
    this.searchControl.valueChanges
      .pipe(
        takeUntilDestroyed(),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((value) =>
          typeof value === 'string' && value.trim()
            ? this.customersService.getCustomers({ search: value, page: 1, pageSize: 10 }).pipe(map((res) => res.items))
            : of([] as CustomerListItem[]),
        ),
      )
      .subscribe((items) => {
        const linkedIds = new Set(this.owners().map((o) => o.id));
        this.ownerOptions.set(items.filter((c) => !linkedIds.has(c.id)));
      });
  }

  // The field never retains a picked object (we reset after linking), so render nothing.
  displayOwner(): string {
    return '';
  }

  onOwnerSelected(event: MatAutocompleteSelectedEvent): void {
    const customer = event.option.value as CustomerListItem;
    this.searchControl.setValue('');
    this.ownerOptions.set([]);
    this.linking.set(true);
    this.ownersService.linkOwner(this.waterId(), customer.id).subscribe({
      next: () => {
        this.toastService.showSuccess(this.translate.instant('waters.owners.link-success'));
        this.linking.set(false);
        this.ownersResource.reload();
      },
      error: () => {
        this.toastService.showError(this.translate.instant('waters.owners.link-failed'));
        this.linking.set(false);
      },
    });
  }

  setPrimary(customerId: number): void {
    this.busyId.set(customerId);
    this.ownersService.setPrimaryOwner(this.waterId(), customerId).subscribe({
      next: () => {
        this.toastService.showSuccess(this.translate.instant('waters.owners.primary-success'));
        this.busyId.set(null);
        this.ownersResource.reload();
      },
      error: () => {
        this.toastService.showError(this.translate.instant('waters.owners.primary-failed'));
        this.busyId.set(null);
      },
    });
  }

  editOwner(customerId: number): void {
    // Return to this water's form (its General tab holds the owners) on cancel/back/save.
    this.router.navigate(['/customers/edit', customerId], {
      queryParams: { backUrl: `/waters/edit/${this.waterId()}` },
    });
  }

  async unlinkOwner(customerId: number): Promise<void> {
    const confirmed = await this.confirmation.open({
      title: this.translate.instant('waters.owners.unlink-title'),
      prompt: this.translate.instant('waters.owners.unlink-confirm'),
    });
    if (!confirmed) return;

    this.busyId.set(customerId);
    try {
      await firstValueFrom(this.ownersService.unlinkOwner(this.waterId(), customerId));
      this.toastService.showSuccess(this.translate.instant('waters.owners.unlink-success'));
      this.ownersResource.reload();
    } catch {
      this.toastService.showError(this.translate.instant('waters.owners.unlink-failed'));
    } finally {
      this.busyId.set(null);
    }
  }
}
